//using in settings and onboarding screens
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import type { InventorySortKey } from '@/pages/Inventory/type';
import type { StillStuckItem } from '@/layout/AppHelpCenter/types';
// Dark values of the --zl-temp-* palette variables used below.
import '@/utils/legacyColor.css';

export const skuSortOptions: Array<{ key: InventorySortKey; label: string }> = [
  { key: 'sku', label: 'SKU' },
  { key: 'team', label: 'Team' },
  { key: 'stock_vs_required_state', label: 'Stock vs. required state' },
  { key: 'stock_planning_state', label: 'Stock planning state' },
  { key: 'last_updated', label: 'Last updated' },
];

export const deviceSortOptions: Array<{
  key: InventorySortKey;
  label: string;
}> = [
  { key: 'device_id', label: 'Device ID' },
  { key: 'location', label: 'Site / location' },
  { key: 'sku', label: 'SKU' },
  { key: 'status', label: 'Status' },
  { key: 'team', label: 'Team' },
  { key: 'shipped_in', label: 'Shipped in' },
  { key: 'activated', label: 'Activated' },
  { key: 'returned', label: 'Returned' },
  { key: 'last_updated', label: 'Last updated' },
];

export const locationTypeList = [
  { value: 'CLINICAL_SITE', label: 'Clinical site' },
  { value: 'HOSPITAL', label: 'Hospital' },
  { value: 'LAB', label: 'Lab' },
  { value: 'RESIDENTIAL', label: 'Residential' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'OTHER', label: 'Other' },
];

export const AssignPermissionOptions = [
  { value: 'CREATE', label: 'Create' },
  { value: 'Create_self', label: 'Create (Self)' },
  { value: 'EDIT', label: 'Edit' },
  { value: 'VIEW', label: 'View' },
  { value: 'NO_ACCESS', label: 'No access' },
  { value: 'EDIT_CONTACTS_ONLY', label: 'Edit (contacts only)' },
];

export const tempratureRangeList = [
  { value: 'custom', label: 'Custom', min: null, max: null },
  { value: 'none', label: 'None', min: null, max: null },
  {
    value: 'cryogenic',
    label: '-150°C to -180°C (Cryogenic)',
    min: -180,
    max: -150,
  },
  {
    value: 'ultra_low_freezer',
    label: '-60°C to -90°C (Ultra low freezer)',
    min: -90,
    max: -60,
  },
  {
    value: 'deep_frozen',
    label: '-40°C to -20°C (Deep frozen)',
    min: -40,
    max: -20,
  },
  { value: 'frozen', label: '-25°C to -15°C (Frozen)', min: -25, max: -15 },
  { value: 'refrigerated', label: '2°C to 8°C (Refrigerated)', min: 2, max: 8 },
  { value: 'crt', label: '15°C to 25°C (CRT)', min: 15, max: 25 },
  { value: 'ambient', label: '15°C to 30°C (Ambient)', min: 15, max: 30 },
];

//this list is used in shipment.jsx of filters
export interface FilterOption {
  id: number | string;
  key: string;
  label: string;
}

export interface FilterGroup {
  group: string;
  items: FilterOption[];
}

export const filtersConfig = {
  topLevelFilters: [
    { id: 17, key: 'saved_filters', label: 'Saved filter sets' },
    { id: 12, key: 'team', label: themeT('team', theme.themeType) },
  ],
  groupedFilters: [
    {
      group: 'Route',
      items: [
        { id: 1, key: 'from_location', label: 'Origin address' },
        { id: 2, key: 'to_location', label: 'Destination address' },
        { id: 23, key: 'origin_state', label: 'Origin state' },
        { id: 24, key: 'destination_state', label: 'Destination state' },
        { id: 25, key: 'location_type', label: 'Location type' },
        { id: 31, key: 'shipper_rtg', label: 'Shipper RTG' },
        { id: 30, key: 'consignee_rtg', label: 'Consignee RTG' },
      ],
    },
    {
      group: 'Alerts & Risk',
      items: [
        { id: 15, key: 'alert_types', label: 'Alerts' },
        { id: 4, key: 'type', label: 'Risk' },
        { id: 29, key: 'alert_assignment', label: 'Alert assignment' },
        {
          id: 22,
          key: 'last_open_alert_aging',
          label: 'Last open alert aging',
        },
      ],
    },
    {
      group: 'Status',
      items: [
        { id: 3, key: 'status', label: 'Progress' },
        { id: 5, key: 'delivery_status', label: 'Timeliness' },
        { id: 21, key: 'pod_status', label: 'POD Status' },
      ],
    },
    {
      group: 'Shipment',
      items: [
        { id: 6, key: 'product', label: 'Product' },
        { id: 7, key: 'product_type', label: 'Product type' },
        { id: 8, key: 'project_id', label: 'Project ID' },
        { id: 16, key: 'temperature', label: 'Temperature' },
        {
          id: 11,
          key: 'courier',
          label: themeT('transportProvider', theme.themeType),
        },
        { id: 28, key: 'sensor', label: 'Sensor' },
        { id: 13, key: 'link_code', label: 'Linked shipments' },
      ],
    },
    {
      group: 'Dates',
      items: [
        { id: 9, key: 'scheduled_pickup_date', label: 'Pickup date' },
        {
          id: 18,
          key: 'customer_defined_ship_date',
          label: 'Customer Defined Ship Date',
        },
        { id: 19, key: 'created_at', label: 'Created date' },
        { id: 10, key: 'scheduled_delivery_date', label: 'Delivery date' },
      ],
    },
    {
      group: 'Other',
      items: [{ id: 14, key: 'ARCHIVED', label: 'Archived' }],
    },
  ],
};

export const filtersList = [
  ...filtersConfig.topLevelFilters,
  ...filtersConfig.groupedFilters.flatMap((group) => group.items),
];

// POD Status dropdown  list
export const PODStatusOptions = [
  { id: 'received', name: 'POD received' },
  { id: 'missing', name: 'POD missing' },
];

// GxP Reports - Triage filter dropdown lists
export const GxPExcursionsOptions = [
  { id: 'none', name: 'None' },
  { id: 'has_excursions', name: 'Has excursions' },
];

export const GxPDataOptions = [
  { id: 'full', name: 'Full' },
  { id: 'missing', name: 'Missing' },
];

export const CURATED_TIMEZONES = [
  'Pacific/Pago_Pago', // UTC-11
  'Pacific/Honolulu', // UTC-10
  'America/Anchorage', // UTC-9
  'America/Los_Angeles', // UTC-8
  'America/Denver', // UTC-7
  'America/Chicago', // UTC-6
  'America/New_York', // UTC-5
  'America/Halifax', // UTC-4
  'America/Sao_Paulo', // UTC-3
  'Atlantic/Azores', // UTC-1
  'Europe/London', // UTC±0
  'Europe/Berlin', // UTC+1
  'Europe/Athens', // UTC+2
  'Africa/Nairobi', // UTC+3
  'Asia/Dubai', // UTC+4
  'Asia/Karachi', // UTC+5
  'Asia/Kolkata', // UTC+5:30
  'Asia/Dhaka', // UTC+6
  'Asia/Bangkok', // UTC+7
  'Asia/Shanghai', // UTC+8
  'Asia/Tokyo', // UTC+9
  'Australia/Sydney', // UTC+10
  'Pacific/Noumea', // UTC+11
  'Pacific/Auckland', // UTC+12
  'Pacific/Tongatapu', // UTC+13
  'Pacific/Kiritimati', // UTC+14
];

export const shipmentStatusDDL = [
  { id: 'Awaiting Pickup', name: 'Awaiting pickup' },
  { id: 'IN-TRANSIT', name: 'In transit' },
  { id: 'OUT_FOR_DELIVERY', name: 'Out for delivery' },
  { id: 'DELIVERY_EXCEPTION', name: 'Delivery exception' },
  { id: 'DELIVERED', name: 'Delivered' },
  { id: 'Not Found', name: 'Not found' },

  { id: 'NO_CARRIER_DATA', name: 'No carrier data' },
  { id: 'TRACKING_ID_ISSUE', name: 'Tracking ID issue' },
];

export const healthStatusDDL = [
  { id: '0', name: 'Critical' },
  { id: '1', name: 'At-risk alert' },
  { id: '2', name: 'No issue' },
];

export const deliveryStatusDDL = [
  { id: 'Early', name: 'Early' },
  { id: 'On time', name: 'On time' },
  { id: 'Late', name: 'Late' },
  { id: 'Unknown', name: 'Other' },
];

export const teams = [
  {
    label: 'Internal medicine',
    value: 'internal_medicine',
    name: 'Internal Medicine',
  },
  { label: 'Hospital', value: 'hospital', name: 'Hospital' },
  { label: 'Oncology', value: 'oncology', name: 'Oncology' },
  { label: 'Vaccines', value: 'vaccines', name: 'Vaccines' },
  {
    label: 'Specialty pharma',
    value: 'specialty_pharma',
    name: 'Specialty Pharma',
  },
];

export const ReportingPeriodList = [
  { label: 'Custom', value: 'Custom' },
  { label: 'Today', value: 'Today' },
  { label: 'Yesterday', value: 'Yesterday' },
  { label: 'Last 24 hours', value: 'Last 24 hours' },
  { label: 'Last 7 days', value: 'Last 7 days' },
  { label: 'Last 30 days', value: 'Last 30 days' },
  { label: 'Last week', value: 'Last week' },
  { label: 'Last month', value: 'Last month' },
  { label: 'Week to date', value: 'Week to date' },
  { label: 'Month to date', value: 'Month to date' },
  { label: 'Quarter to date', value: 'Quarter to date' },
  { label: 'Year to date', value: 'Year to date' },
  { label: 'All time', value: 'All time' },
];

export const metricsTimePeriod = [
  { value: 'month', label: 'Per month' },
  { value: 'quarter', label: 'Per quarter' },
  { value: 'year', label: 'Per year' },
];
export const onTimeSettingsPeriod = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];
export const distributionHistogram = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '4', label: '4' },
  { value: '8', label: '8' },
  { value: '12', label: '12' },
];

export const sensorIgnoreTime = [
  { label: '0 min', value: 0 },
  { label: '10 min', value: 10 },
  { label: '20 min', value: 20 },
  { label: '30 min', value: 30 },
  { label: '40 min', value: 40 },
  { label: '50 min', value: 50 },
  { label: '60 min', value: 60 },
  { label: '70 min', value: 70 },
  { label: '80 min', value: 80 },
  { label: '90 min', value: 90 },
  { label: '100 min', value: 100 },
  { label: '110 min', value: 110 },
  { label: '120 min', value: 120 },
];
export const lastOpenAlertAgingOptions = [
  { id: 'lt_7', name: '<7 days' },
  { id: '7_30', name: '7 - 30 days' },
  { id: 'gt_30', name: '30+ days' },
];
export const HometempratureList = [
  { id: 'custom', name: 'Custom', color: 'var(--text-primary)' },
  { id: 'none', name: 'None', color: '#757575' },
  { id: 'ambient', name: '15° to 30°C', name2: 'Ambient', color: '#9BD7B8' },
  { id: 'crt', name: '15° to 25°C', name2: 'CRT', color: '#1FAE86' },
  {
    id: 'refrigerated',
    name: '2° to 8°C',
    name2: 'Refrigerated',
    color: '#0BA9C3',
  },
  { id: 'frozen', name: '-25° to -15°C', name2: 'Frozen', color: '#1C64F2' },
  {
    id: 'deep_frozen',
    name: '-40° to -20°C',
    name2: 'Deep frozen',
    color: 'var(--zl-temp-deep-frozen, #1C2D84)',
  },
  {
    id: 'ultra_low_freezer',
    name: '-60° to -90°C',
    name2: 'Ultra low freezer',
    color: 'var(--zl-temp-ultra-low, #4B2D89)',
  },
  {
    id: 'cryogenic',
    name: '-150° to -180°C',
    name2: 'Cryogenic',
    color: 'var(--zl-temp-cryogenic, #2A1756)',
  },
];

export const RiskLevelList = [
  { id: 'no_issue', name: 'No issue', color: '' },
  { id: 'critical', name: 'Critical', color: '#CC1031' },
  { id: 'at_risk', name: 'At-risk', color: '#F6BC2F' },
];

export const recipientList = [
  {
    id: 'TRANSPORT_PROVIDER_PRIMARY',
    name: 'Transport provider primary contact',
  },
  { id: 'DESTINATION_PRIMARY', name: 'Destination primary contact' },
  { id: 'ORIGIN_PRIMARY', name: 'Origin primary contact' },
  { id: 'CONTROLLING_OFFICE', name: 'Controlling office' },
];

// x
export const prefferedLanguages = [
  { id: 'Bulgarian', name: 'Bulgarian' },
  { id: 'Catalan', name: 'Catalan' },
  { id: 'Chinese', name: 'Chinese' },
  { id: 'Danish', name: 'Danish' },
  { id: 'Dutch', name: 'Dutch' },
  { id: 'English', name: 'English' },
  { id: 'Finnish', name: 'Finnish' },
  { id: 'French', name: 'French' },
  { id: 'German', name: 'German' },
  { id: 'Greek', name: 'Greek' },
  { id: 'Hindi', name: 'Hindi' },
  { id: 'Hungarian', name: 'Hungarian' },
  { id: 'Indonesian', name: 'Indonesian' },
  { id: 'Italian', name: 'Italian' },
  { id: 'Japanese', name: 'Japanese' },
  { id: 'Korean', name: 'Korean' },
  { id: 'Malay', name: 'Malay' },
  { id: 'Norwegian', name: 'Norwegian' },
  { id: 'Polish', name: 'Polish' },
  { id: 'Portuguese', name: 'Portuguese' },
  { id: 'Romanian', name: 'Romanian' },
  { id: 'Russian', name: 'Russian' },
  { id: 'Slovak', name: 'Slovak' },
  { id: 'Spanish', name: 'Spanish' },
  { id: 'Swedish', name: 'Swedish' },
  { id: 'Thai', name: 'Thai' },
  { id: 'Turkish', name: 'Turkish' },
  { id: 'Vietnamese', name: 'Vietnamese' },
];

export type AlertType =
  | 'CRITICAL_ALERT'
  | 'PICKUP_DELIVERY'
  | 'AT_LOCATION'
  | 'ETA_BASED'
  | 'AI_ACTION'
  | 'LOW_INVENTORY'
  | 'TEMPERATURE_GXP';

export interface TriggerType {
  key: string;
  id: AlertType;
  icon?: string;
  title?: string;
  text?: string;
}

export const notificationsTriggerType: TriggerType[] = [
  {
    title: 'Alert event',
    text: 'Notification when an At-risk or Critical alert has been detected',
    icon: 'WarningIcon',
    key: '0',
    id: 'CRITICAL_ALERT',
  },
  {
    title: 'Status progress',
    text: 'Notification when there is a shipment status change',
    icon: 'TruckIcon',
    key: '1',
    id: 'PICKUP_DELIVERY',
  },
  {
    title: 'ETA change',
    text: 'Notification when projected ETA changes by a set time',
    icon: 'ClockIcon',
    id: 'ETA_BASED',
    key: '2',
  },
  {
    title: 'Location',
    text: 'Notification when the shipment arrives at a specific location',
    icon: 'LocationIcon',
    key: '3',

    id: 'AT_LOCATION',
  },
  {
    title: 'AI action',
    text: 'Notification when an AI action was performed',
    icon: 'AiStar',
    key: '4',
    id: 'AI_ACTION',
  },
  {
    title: 'Inventory low',
    text: 'Notification when an inventory item’s target quantity drops below its target level',
    icon: 'LowInventoryIcon',
    key: '5',
    id: 'LOW_INVENTORY',
  },
  {
    title: 'Temperature GxP report',
    text: 'Notification when a temperature report changes state',
    icon: 'TemperatureGxpReportIcon',
    key: '6',
    id: 'TEMPERATURE_GXP',
  },
];

export interface GxPReportEventOption {
  value: string;
  label: string;
}

export const GxPReportEventOptions: GxPReportEventOption[] = [
  { value: 'EXCURSION_DETECTED', label: 'Excursion detected' },
  { value: 'READY_FOR_REVIEW', label: 'Ready for review' },
  { value: 'ON_HOLD_MISSING_DATA', label: 'On hold, missing data' },
  {
    value: 'FINALIZED_MISSING_DATA',
    label: 'Finalized with missing data (hold expired)',
  },
  {
    value: 'PUBLISHED',
    label: 'Published',
  },
];

export interface NotifyViaOption {
  type: 'both' | 'ai' | 'non-ai';
  value: string;
  label: string;
  isDisabled: boolean;
  text: string;
  icon: boolean;
}

export const AllNotifyViaOptions: NotifyViaOption[] = [
  {
    type: 'both',
    value: 'EMAIL',
    label: 'Email',
    isDisabled: false,
    text: '',
    icon: false,
  },
  {
    type: 'both',
    value: 'MOBILE',
    label: 'SMS',
    isDisabled: false,
    text: '',
    icon: false,
  },
  {
    type: 'non-ai',
    value: 'SLACK',
    label: 'Slack',
    isDisabled: false,
    text: '',
    icon: false,
  },
  {
    type: 'non-ai',
    value: 'TEAMS',
    label: 'Teams',
    isDisabled: false,
    text: '',
    icon: false,
  },
  {
    type: 'non-ai',
    value: 'WHATSAPP',
    label: 'WhatsApp',
    isDisabled: false,
    text: '',
    icon: false,
  },
  {
    type: 'ai',
    value: 'AI_EMAIL',
    label: 'Email using AI-assistant',
    isDisabled: false,
    text: '',
    icon: true,
  },
  {
    type: 'ai',
    value: 'AI_CALL',
    label: 'Call using AI-assistant',
    isDisabled: false,
    text: '',
    icon: true,
  },
];

export const ETAChangedByList = [
  { value: '15 minutes', label: '15 minutes' },
  { value: '30 minutes', label: '30 minutes' },
  { value: '1 hour', label: '1 hour' },
  { value: 'Over 2 hours', label: 'Over 2 hours' },
];

export const FrequencyOptions = [
  { value: 'REALTIME', label: 'Real Time' },
  { value: 'ONCE_A_DAY', label: 'Once a day' },
  { value: 'TWICE_A_DAY', label: 'Twice a day' },
  { value: 'WEEKLY', label: 'Weekly' },
];

export const DayOptions = [
  { value: 'Sunday', label: 'Sun' },
  { value: 'Monday', label: 'Mon' },
  { value: 'Tuesday', label: 'Tue' },
  { value: 'Wednesday', label: 'Wed' },
  { value: 'Thursday', label: 'Thu' },
  { value: 'Friday', label: 'Fri' },
  { value: 'Saturday', label: 'Sat' },
];

export const NotificationApplyOptions = [
  { id: 1, label: 'Product', keyName: 'products' },
  {
    id: 2,
    label: theme.themeType === 2 ? 'Carrier' : 'Transport provider',
    keyName: 'filter_transport_providers',
  },
  { id: 3, label: 'Origin', keyName: 'filter_origins' },
  { id: 4, label: 'Destination', keyName: 'filter_destinations' },
  {
    id: 5,
    label: theme.themeType === 2 ? 'Clients' : 'Teams',
    keyName: 'filter_teams',
  },
];

export const statesAndCities = [
  {
    id: 'ca-001',
    name: 'California',
    cities: [
      {
        id: 'ca-001_Los_Angeles',
        city_type: 'metro',
        city_name: 'Los Angeles',
        display_name: 'Metro - Los Angeles',
        original_city_type: 'Metro',
        original_city_name: 'Los Angeles',
      },
      {
        id: 'ca-001_San_Diego',
        city_type: 'metro',
        city_name: 'San Diego',
        display_name: 'Metro - San Diego',
        original_city_type: 'Metro',
        original_city_name: 'San Diego',
      },
      {
        id: 'ca-001_San_Jose',
        city_type: 'metro',
        city_name: 'San Jose',
        display_name: 'Metro - San Jose',
        original_city_type: 'Metro',
        original_city_name: 'San Jose',
      },
      {
        id: 'ca-001_San_Francisco',
        city_type: 'metro',
        city_name: 'San Francisco',
        display_name: 'Metro - San Francisco',
        original_city_type: 'Metro',
        original_city_name: 'San Francisco',
      },
      {
        id: 'ca-001_Fresno',
        city_type: 'metro',
        city_name: 'Fresno',
        display_name: 'Metro - Fresno',
        original_city_type: 'Metro',
        original_city_name: 'Fresno',
      },
      {
        id: 'ca-001_Sacramento',
        city_type: 'metro',
        city_name: 'Sacramento',
        display_name: 'Metro - Sacramento',
        original_city_type: 'Metro',
        original_city_name: 'Sacramento',
      },
    ],
    total_cities: 6,
  },
  {
    id: 'tx-002',
    name: 'Texas',
    cities: [
      {
        id: 'tx-002_Houston',
        city_type: 'metro',
        city_name: 'Houston',
        display_name: 'Metro - Houston',
        original_city_type: 'Metro',
        original_city_name: 'Houston',
      },
      {
        id: 'tx-002_Dallas',
        city_type: 'metro',
        city_name: 'Dallas',
        display_name: 'Metro - Dallas',
        original_city_type: 'Metro',
        original_city_name: 'Dallas',
      },
      {
        id: 'tx-002_Austin',
        city_type: 'metro',
        city_name: 'Austin',
        display_name: 'Metro - Austin',
        original_city_type: 'Metro',
        original_city_name: 'Austin',
      },
      {
        id: 'tx-002_San_Antonio',
        city_type: 'metro',
        city_name: 'San Antonio',
        display_name: 'Metro - San Antonio',
        original_city_type: 'Metro',
        original_city_name: 'San Antonio',
      },
      {
        id: 'tx-002_Fort_Worth',
        city_type: 'metro',
        city_name: 'Fort Worth',
        display_name: 'Metro - Fort Worth',
        original_city_type: 'Metro',
        original_city_name: 'Fort Worth',
      },
      {
        id: 'tx-002_El_Paso',
        city_type: 'metro',
        city_name: 'El Paso',
        display_name: 'Metro - El Paso',
        original_city_type: 'Metro',
        original_city_name: 'El Paso',
      },
    ],
    total_cities: 6,
  },
  {
    id: 'fl-003',
    name: 'Florida',
    cities: [
      {
        id: 'fl-003_Miami',
        city_type: 'metro',
        city_name: 'Miami',
        display_name: 'Metro - Miami',
        original_city_type: 'Metro',
        original_city_name: 'Miami',
      },
      {
        id: 'fl-003_Orlando',
        city_type: 'metro',
        city_name: 'Orlando',
        display_name: 'Metro - Orlando',
        original_city_type: 'Metro',
        original_city_name: 'Orlando',
      },
      {
        id: 'fl-003_Tampa',
        city_type: 'metro',
        city_name: 'Tampa',
        display_name: 'Metro - Tampa',
        original_city_type: 'Metro',
        original_city_name: 'Tampa',
      },
      {
        id: 'fl-003_Jacksonville',
        city_type: 'metro',
        city_name: 'Jacksonville',
        display_name: 'Metro - Jacksonville',
        original_city_type: 'Metro',
        original_city_name: 'Jacksonville',
      },
      {
        id: 'fl-003_St_Petersburg',
        city_type: 'metro',
        city_name: 'St. Petersburg',
        display_name: 'Metro - St. Petersburg',
        original_city_type: 'Metro',
        original_city_name: 'St. Petersburg',
      },
    ],
    total_cities: 5,
  },
  {
    id: 'ny-004',
    name: 'New York',
    cities: [
      {
        id: 'ny-004_New_York_City',
        city_type: 'metro',
        city_name: 'New York City',
        display_name: 'Metro - New York City',
        original_city_type: 'Metro',
        original_city_name: 'New York City',
      },
      {
        id: 'ny-004_Buffalo',
        city_type: 'metro',
        city_name: 'Buffalo',
        display_name: 'Metro - Buffalo',
        original_city_type: 'Metro',
        original_city_name: 'Buffalo',
      },
      {
        id: 'ny-004_Rochester',
        city_type: 'metro',
        city_name: 'Rochester',
        display_name: 'Metro - Rochester',
        original_city_type: 'Metro',
        original_city_name: 'Rochester',
      },
      {
        id: 'ny-004_Yonkers',
        city_type: 'metro',
        city_name: 'Yonkers',
        display_name: 'Metro - Yonkers',
        original_city_type: 'Metro',
        original_city_name: 'Yonkers',
      },
      {
        id: 'ny-004_Syracuse',
        city_type: 'metro',
        city_name: 'Syracuse',
        display_name: 'Metro - Syracuse',
        original_city_type: 'Metro',
        original_city_name: 'Syracuse',
      },
    ],
    total_cities: 5,
  },
  {
    id: 'il-005',
    name: 'Illinois',
    cities: [
      {
        id: 'il-005_Chicago',
        city_type: 'metro',
        city_name: 'Chicago',
        display_name: 'Metro - Chicago',
        original_city_type: 'Metro',
        original_city_name: 'Chicago',
      },
      {
        id: 'il-005_Springfield',
        city_type: 'metro',
        city_name: 'Springfield',
        display_name: 'Metro - Springfield',
        original_city_type: 'Metro',
        original_city_name: 'Springfield',
      },
      {
        id: 'il-005_Peoria',
        city_type: 'metro',
        city_name: 'Peoria',
        display_name: 'Metro - Peoria',
        original_city_type: 'Metro',
        original_city_name: 'Peoria',
      },
      {
        id: 'il-005_Naperville',
        city_type: 'metro',
        city_name: 'Naperville',
        display_name: 'Metro - Naperville',
        original_city_type: 'Metro',
        original_city_name: 'Naperville',
      },
    ],
    total_cities: 4,
  },
  {
    id: 'pa-006',
    name: 'Pennsylvania',
    cities: [
      {
        id: 'pa-006_Philadelphia',
        city_type: 'metro',
        city_name: 'Philadelphia',
        display_name: 'Metro - Philadelphia',
        original_city_type: 'Metro',
        original_city_name: 'Philadelphia',
      },
      {
        id: 'pa-006_Pittsburgh',
        city_type: 'metro',
        city_name: 'Pittsburgh',
        display_name: 'Metro - Pittsburgh',
        original_city_type: 'Metro',
        original_city_name: 'Pittsburgh',
      },
      {
        id: 'pa-006_Allentown',
        city_type: 'metro',
        city_name: 'Allentown',
        display_name: 'Metro - Allentown',
        original_city_type: 'Metro',
        original_city_name: 'Allentown',
      },
      {
        id: 'pa-006_Harrisburg',
        city_type: 'metro',
        city_name: 'Harrisburg',
        display_name: 'Metro - Harrisburg',
        original_city_type: 'Metro',
        original_city_name: 'Harrisburg',
      },
    ],
    total_cities: 4,
  },
  {
    id: 'oh-007',
    name: 'Ohio',
    cities: [
      {
        id: 'oh-007_Columbus',
        city_type: 'metro',
        city_name: 'Columbus',
        display_name: 'Metro - Columbus',
        original_city_type: 'Metro',
        original_city_name: 'Columbus',
      },
      {
        id: 'oh-007_Cleveland',
        city_type: 'metro',
        city_name: 'Cleveland',
        display_name: 'Metro - Cleveland',
        original_city_type: 'Metro',
        original_city_name: 'Cleveland',
      },
      {
        id: 'oh-007_Cincinnati',
        city_type: 'metro',
        city_name: 'Cincinnati',
        display_name: 'Metro - Cincinnati',
        original_city_type: 'Metro',
        original_city_name: 'Cincinnati',
      },
      {
        id: 'oh-007_Toledo',
        city_type: 'metro',
        city_name: 'Toledo',
        display_name: 'Metro - Toledo',
        original_city_type: 'Metro',
        original_city_name: 'Toledo',
      },
      {
        id: 'oh-007_Akron',
        city_type: 'metro',
        city_name: 'Akron',
        display_name: 'Metro - Akron',
        original_city_type: 'Metro',
        original_city_name: 'Akron',
      },
    ],
    total_cities: 5,
  },
  {
    id: 'ga-008',
    name: 'Georgia',
    cities: [
      {
        id: 'ga-008_Atlanta',
        city_type: 'metro',
        city_name: 'Atlanta',
        display_name: 'Metro - Atlanta',
        original_city_type: 'Metro',
        original_city_name: 'Atlanta',
      },
      {
        id: 'ga-008_Augusta',
        city_type: 'metro',
        city_name: 'Augusta',
        display_name: 'Metro - Augusta',
        original_city_type: 'Metro',
        original_city_name: 'Augusta',
      },
      {
        id: 'ga-008_Savannah',
        city_type: 'metro',
        city_name: 'Savannah',
        display_name: 'Metro - Savannah',
        original_city_type: 'Metro',
        original_city_name: 'Savannah',
      },
      {
        id: 'ga-008_Columbus',
        city_type: 'metro',
        city_name: 'Columbus',
        display_name: 'Metro - Columbus',
        original_city_type: 'Metro',
        original_city_name: 'Columbus',
      },
      {
        id: 'ga-008_Macon',
        city_type: 'metro',
        city_name: 'Macon',
        display_name: 'Metro - Macon',
        original_city_type: 'Metro',
        original_city_name: 'Macon',
      },
    ],
    total_cities: 5,
  },
  {
    id: 'nc-009',
    name: 'North Carolina',
    cities: [
      {
        id: 'nc-009_Charlotte',
        city_type: 'metro',
        city_name: 'Charlotte',
        display_name: 'Metro - Charlotte',
        original_city_type: 'Metro',
        original_city_name: 'Charlotte',
      },
      {
        id: 'nc-009_Raleigh',
        city_type: 'metro',
        city_name: 'Raleigh',
        display_name: 'Metro - Raleigh',
        original_city_type: 'Metro',
        original_city_name: 'Raleigh',
      },
      {
        id: 'nc-009_Greensboro',
        city_type: 'metro',
        city_name: 'Greensboro',
        display_name: 'Metro - Greensboro',
        original_city_type: 'Metro',
        original_city_name: 'Greensboro',
      },
      {
        id: 'nc-009_Winston_Salem',
        city_type: 'metro',
        city_name: 'Winston-Salem',
        display_name: 'Metro - Winston-Salem',
        original_city_type: 'Metro',
        original_city_name: 'Winston-Salem',
      },
      {
        id: 'nc-009_Durham',
        city_type: 'metro',
        city_name: 'Durham',
        display_name: 'Metro - Durham',
        original_city_type: 'Metro',
        original_city_name: 'Durham',
      },
    ],
    total_cities: 5,
  },
  {
    id: 'mi-010',
    name: 'Michigan',
    cities: [
      {
        id: 'mi-010_Detroit',
        city_type: 'metro',
        city_name: 'Detroit',
        display_name: 'Metro - Detroit',
        original_city_type: 'Metro',
        original_city_name: 'Detroit',
      },
      {
        id: 'mi-010_Grand_Rapids',
        city_type: 'metro',
        city_name: 'Grand Rapids',
        display_name: 'Metro - Grand Rapids',
        original_city_type: 'Metro',
        original_city_name: 'Grand Rapids',
      },
      {
        id: 'mi-010_Warren',
        city_type: 'metro',
        city_name: 'Warren',
        display_name: 'Metro - Warren',
        original_city_type: 'Metro',
        original_city_name: 'Warren',
      },
      {
        id: 'mi-010_Flint',
        city_type: 'metro',
        city_name: 'Flint',
        display_name: 'Metro - Flint',
        original_city_type: 'Metro',
        original_city_name: 'Flint',
      },
      {
        id: 'mi-010_Lansing',
        city_type: 'metro',
        city_name: 'Lansing',
        display_name: 'Metro - Lansing',
        original_city_type: 'Metro',
        original_city_name: 'Lansing',
      },
    ],
    total_cities: 5,
  },
  {
    id: 'nj-011',
    name: 'New Jersey',
    cities: [
      {
        id: 'nj-011_Newark',
        city_type: 'metro',
        city_name: 'Newark',
        display_name: 'Metro - Newark',
        original_city_type: 'Metro',
        original_city_name: 'Newark',
      },
      {
        id: 'nj-011_Jersey_City',
        city_type: 'metro',
        city_name: 'Jersey City',
        display_name: 'Metro - Jersey City',
        original_city_type: 'Metro',
        original_city_name: 'Jersey City',
      },
      {
        id: 'nj-011_Paterson',
        city_type: 'metro',
        city_name: 'Paterson',
        display_name: 'Metro - Paterson',
        original_city_type: 'Metro',
        original_city_name: 'Paterson',
      },
      {
        id: 'nj-011_Elizabeth',
        city_type: 'metro',
        city_name: 'Elizabeth',
        display_name: 'Metro - Elizabeth',
        original_city_type: 'Metro',
        original_city_name: 'Elizabeth',
      },
    ],
    total_cities: 4,
  },
  {
    id: 'va-012',
    name: 'Virginia',
    cities: [
      {
        id: 'va-012_Virginia_Beach',
        city_type: 'metro',
        city_name: 'Virginia Beach',
        display_name: 'Metro - Virginia Beach',
        original_city_type: 'Metro',
        original_city_name: 'Virginia Beach',
      },
      {
        id: 'va-012_Norfolk',
        city_type: 'metro',
        city_name: 'Norfolk',
        display_name: 'Metro - Norfolk',
        original_city_type: 'Metro',
        original_city_name: 'Norfolk',
      },
      {
        id: 'va-012_Richmond',
        city_type: 'metro',
        city_name: 'Richmond',
        display_name: 'Metro - Richmond',
        original_city_type: 'Metro',
        original_city_name: 'Richmond',
      },
      {
        id: 'va-012_Newport_News',
        city_type: 'metro',
        city_name: 'Newport News',
        display_name: 'Metro - Newport News',
        original_city_type: 'Metro',
        original_city_name: 'Newport News',
      },
      {
        id: 'va-012_Alexandria',
        city_type: 'metro',
        city_name: 'Alexandria',
        display_name: 'Metro - Alexandria',
        original_city_type: 'Metro',
        original_city_name: 'Alexandria',
      },
    ],
    total_cities: 5,
  },
  {
    id: 'wa-013',
    name: 'Washington',
    cities: [
      {
        id: 'wa-013_Seattle',
        city_type: 'metro',
        city_name: 'Seattle',
        display_name: 'Metro - Seattle',
        original_city_type: 'Metro',
        original_city_name: 'Seattle',
      },
      {
        id: 'wa-013_Spokane',
        city_type: 'metro',
        city_name: 'Spokane',
        display_name: 'Metro - Spokane',
        original_city_type: 'Metro',
        original_city_name: 'Spokane',
      },
      {
        id: 'wa-013_Tacoma',
        city_type: 'metro',
        city_name: 'Tacoma',
        display_name: 'Metro - Tacoma',
        original_city_type: 'Metro',
        original_city_name: 'Tacoma',
      },
      {
        id: 'wa-013_Vancouver',
        city_type: 'metro',
        city_name: 'Vancouver',
        display_name: 'Metro - Vancouver',
        original_city_type: 'Metro',
        original_city_name: 'Vancouver',
      },
      {
        id: 'wa-013_Bellevue',
        city_type: 'metro',
        city_name: 'Bellevue',
        display_name: 'Metro - Bellevue',
        original_city_type: 'Metro',
        original_city_name: 'Bellevue',
      },
    ],
    total_cities: 5,
  },
  {
    id: 'ma-014',
    name: 'Massachusetts',
    cities: [
      {
        id: 'ma-014_Boston',
        city_type: 'metro',
        city_name: 'Boston',
        display_name: 'Metro - Boston',
        original_city_type: 'Metro',
        original_city_name: 'Boston',
      },
      {
        id: 'ma-014_Worcester',
        city_type: 'metro',
        city_name: 'Worcester',
        display_name: 'Metro - Worcester',
        original_city_type: 'Metro',
        original_city_name: 'Worcester',
      },
      {
        id: 'ma-014_Springfield',
        city_type: 'metro',
        city_name: 'Springfield',
        display_name: 'Metro - Springfield',
        original_city_type: 'Metro',
        original_city_name: 'Springfield',
      },
      {
        id: 'ma-014_Cambridge',
        city_type: 'metro',
        city_name: 'Cambridge',
        display_name: 'Metro - Cambridge',
        original_city_type: 'Metro',
        original_city_name: 'Cambridge',
      },
      {
        id: 'ma-014_Lowell',
        city_type: 'metro',
        city_name: 'Lowell',
        display_name: 'Metro - Lowell',
        original_city_type: 'Metro',
        original_city_name: 'Lowell',
      },
    ],
    total_cities: 5,
  },
  {
    id: 'co-015',
    name: 'Colorado',
    cities: [
      {
        id: 'co-015_Denver',
        city_type: 'metro',
        city_name: 'Denver',
        display_name: 'Metro - Denver',
        original_city_type: 'Metro',
        original_city_name: 'Denver',
      },
      {
        id: 'co-015_Colorado_Springs',
        city_type: 'metro',
        city_name: 'Colorado Springs',
        display_name: 'Metro - Colorado Springs',
        original_city_type: 'Metro',
        original_city_name: 'Colorado Springs',
      },
      {
        id: 'co-015_Aurora',
        city_type: 'metro',
        city_name: 'Aurora',
        display_name: 'Metro - Aurora',
        original_city_type: 'Metro',
        original_city_name: 'Aurora',
      },
    ],
  },
];

export const stageOfDevelopmentValueList = [
  { value: 'Research', label: 'Research' },
  { value: 'Clinical', label: 'Clinical' },
  { value: 'Commercial', label: 'Commercial' },
];

export const commercialValueList = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
];

export const HomeCalendarDateType = [
  { value: 'created_at', label: 'Created date (default)' },
  { value: 'scheduled_pickup_date', label: 'Pickup date' },
  { value: 'scheduled_delivery_date', label: 'Delivery date' },
];

export const homeChartsList = [
  { id: 'shipments', label: 'Shipments' },
  { id: 'otd', label: 'On-time delivery (OTD)' },
  { id: 'otp', label: 'On-time pickup (OTP)' },
  { id: 'schedule_adherence', label: 'Schedule adherence' },
  { id: 'transit_time', label: 'Transit time' },
  { id: 'dwell_time', label: 'Dwell time' },
  {
    id: 'transport_provider_exceptions',
    label: 'Transport provider exceptions',
  },
  { id: 'alerts', label: 'Alerts' },
  { id: 'invoice_disputes', label: 'Invoice disputes' },
  { id: 'cycle_time', label: 'Cycle time' },
];

//used in logiblocks manual execution
export const manualTriggerList = [
  { value: 'manual', label: 'Manual button' },
  { value: 'slack', label: 'Slack' },
  { value: 'teams', label: 'Microsoft Teams' },
];

//used in logiblocks inbound event
export const inboundEventTypeList = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email', disabled: true },
  { value: 'sms', label: 'SMS', disabled: true },
  { value: 'tracking_link', label: 'Tracking link' },
];

export const trackingLinkTypeList = [
  { value: 'address_update', label: 'Address update' },
  { value: 'delivery_time_update', label: 'Delivery time update' },
];

//USED IN TRACKING LINK

export const COUNTRY_OPTIONS = [
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
  { label: 'Mexico', value: 'MX' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'Germany', value: 'DE' },
  { label: 'Afghanistan', value: 'AF' },
  { label: 'Åland Islands', value: 'AX' },
  { label: 'Albania', value: 'AL' },
  { label: 'Algeria', value: 'DZ' },
  { label: 'American Samoa', value: 'AS' },
  { label: 'Andorra', value: 'AD' },
  { label: 'Angola', value: 'AO' },
  { label: 'Anguilla', value: 'AI' },
  { label: 'Antarctica', value: 'AQ' },
  { label: 'Antigua and Barbuda', value: 'AG' },
  { label: 'Argentina', value: 'AR' },
  { label: 'Armenia', value: 'AM' },
  { label: 'Aruba', value: 'AW' },
  { label: 'Australia', value: 'AU' },
  { label: 'Austria', value: 'AT' },
  { label: 'Azerbaijan', value: 'AZ' },
  { label: 'Bahamas', value: 'BS' },
  { label: 'Bahrain', value: 'BH' },
  { label: 'Bangladesh', value: 'BD' },
  { label: 'Barbados', value: 'BB' },
  { label: 'Belarus', value: 'BY' },
  { label: 'Belgium', value: 'BE' },
  { label: 'Belize', value: 'BZ' },
  { label: 'Benin', value: 'BJ' },
  { label: 'Bermuda', value: 'BM' },
  { label: 'Bhutan', value: 'BT' },
  { label: 'Bolivia, Plurinational State of', value: 'BO' },
  { label: 'Bonaire, Sint Eustatius and Saba', value: 'BQ' },
  { label: 'Bosnia and Herzegovina', value: 'BA' },
  { label: 'Botswana', value: 'BW' },
  { label: 'Bouvet Island', value: 'BV' },
  { label: 'Brazil', value: 'BR' },
  { label: 'British Indian Ocean Territory', value: 'IO' },
  { label: 'Brunei Darussalam', value: 'BN' },
  { label: 'Bulgaria', value: 'BG' },
  { label: 'Burkina Faso', value: 'BF' },
  { label: 'Burundi', value: 'BI' },
  { label: 'Cambodia', value: 'KH' },
  { label: 'Cameroon', value: 'CM' },
  { label: 'Cape Verde', value: 'CV' },
  { label: 'Cayman Islands', value: 'KY' },
  { label: 'Central African Republic', value: 'CF' },
  { label: 'Chad', value: 'TD' },
  { label: 'Chile', value: 'CL' },
  { label: 'China', value: 'CN' },
  { label: 'Christmas Island', value: 'CX' },
  { label: 'Cocos (Keeling) Islands', value: 'CC' },
  { label: 'Colombia', value: 'CO' },
  { label: 'Comoros', value: 'KM' },
  { label: 'Congo', value: 'CG' },
  { label: 'Congo, The Democratic Republic of the', value: 'CD' },
  { label: 'Cook Islands', value: 'CK' },
  { label: 'Costa Rica', value: 'CR' },
  { label: "Côte d'Ivoire", value: 'CI' },
  { label: 'Croatia', value: 'HR' },
  { label: 'Cuba', value: 'CU' },
  { label: 'Curaçao', value: 'CW' },
  { label: 'Cyprus', value: 'CY' },
  { label: 'Czech Republic', value: 'CZ' },
  { label: 'Denmark', value: 'DK' },
  { label: 'Djibouti', value: 'DJ' },
  { label: 'Dominica', value: 'DM' },
  { label: 'Dominican Republic', value: 'DO' },
  { label: 'Ecuador', value: 'EC' },
  { label: 'Egypt', value: 'EG' },
  { label: 'El Salvador', value: 'SV' },
  { label: 'Equatorial Guinea', value: 'GQ' },
  { label: 'Eritrea', value: 'ER' },
  { label: 'Estonia', value: 'EE' },
  { label: 'Ethiopia', value: 'ET' },
  { label: 'Falkland Islands (Malvinas)', value: 'FK' },
  { label: 'Faroe Islands', value: 'FO' },
  { label: 'Fiji', value: 'FJ' },
  { label: 'Finland', value: 'FI' },
  { label: 'France', value: 'FR' },
  { label: 'French Guiana', value: 'GF' },
  { label: 'French Polynesia', value: 'PF' },
  { label: 'French Southern Territories', value: 'TF' },
  { label: 'Gabon', value: 'GA' },
  { label: 'Gambia', value: 'GM' },
  { label: 'Georgia', value: 'GE' },
  { label: 'Ghana', value: 'GH' },
  { label: 'Gibraltar', value: 'GI' },
  { label: 'Greece', value: 'GR' },
  { label: 'Greenland', value: 'GL' },
  { label: 'Grenada', value: 'GD' },
  { label: 'Guadeloupe', value: 'GP' },
  { label: 'Guam', value: 'GU' },
  { label: 'Guatemala', value: 'GT' },
  { label: 'Guernsey', value: 'GG' },
  { label: 'Guinea', value: 'GN' },
  { label: 'Guinea-Bissau', value: 'GW' },
  { label: 'Guyana', value: 'GY' },
  { label: 'Haiti', value: 'HT' },
  { label: 'Heard Island and McDonald Islands', value: 'HM' },
  { label: 'Holy See (Vatican City State)', value: 'VA' },
  { label: 'Honduras', value: 'HN' },
  { label: 'Hong Kong', value: 'HK' },
  { label: 'Hungary', value: 'HU' },
  { label: 'Iceland', value: 'IS' },
  { label: 'India', value: 'IN' },
  { label: 'Indonesia', value: 'ID' },
  { label: 'Iran, Islamic Republic of', value: 'IR' },
  { label: 'Iraq', value: 'IQ' },
  { label: 'Ireland', value: 'IE' },
  { label: 'Isle of Man', value: 'IM' },
  { label: 'Israel', value: 'IL' },
  { label: 'Italy', value: 'IT' },
  { label: 'Jamaica', value: 'JM' },
  { label: 'Japan', value: 'JP' },
  { label: 'Jersey', value: 'JE' },
  { label: 'Jordan', value: 'JO' },
  { label: 'Kazakhstan', value: 'KZ' },
  { label: 'Kenya', value: 'KE' },
  { label: 'Kiribati', value: 'KI' },
  { label: "Korea, Democratic People's Republic of", value: 'KP' },
  { label: 'Korea, Republic of', value: 'KR' },
  { label: 'Kuwait', value: 'KW' },
  { label: 'Kyrgyzstan', value: 'KG' },
  { label: "Lao People's Democratic Republic", value: 'LA' },
  { label: 'Latvia', value: 'LV' },
  { label: 'Lebanon', value: 'LB' },
  { label: 'Lesotho', value: 'LS' },
  { label: 'Liberia', value: 'LR' },
  { label: 'Libya', value: 'LY' },
  { label: 'Liechtenstein', value: 'LI' },
  { label: 'Lithuania', value: 'LT' },
  { label: 'Luxembourg', value: 'LU' },
  { label: 'Macao', value: 'MO' },
  { label: 'Macedonia, Republic of', value: 'MK' },
  { label: 'Madagascar', value: 'MG' },
  { label: 'Malawi', value: 'MW' },
  { label: 'Malaysia', value: 'MY' },
  { label: 'Maldives', value: 'MV' },
  { label: 'Mali', value: 'ML' },
  { label: 'Malta', value: 'MT' },
  { label: 'Marshall Islands', value: 'MH' },
  { label: 'Martinique', value: 'MQ' },
  { label: 'Mauritania', value: 'MR' },
  { label: 'Mauritius', value: 'MU' },
  { label: 'Mayotte', value: 'YT' },
  { label: 'Micronesia, Federated States of', value: 'FM' },
  { label: 'Moldova, Republic of', value: 'MD' },
  { label: 'Monaco', value: 'MC' },
  { label: 'Mongolia', value: 'MN' },
  { label: 'Montenegro', value: 'ME' },
  { label: 'Montserrat', value: 'MS' },
  { label: 'Morocco', value: 'MA' },
  { label: 'Mozambique', value: 'MZ' },
  { label: 'Myanmar', value: 'MM' },
  { label: 'Namibia', value: 'NA' },
  { label: 'Nauru', value: 'NR' },
  { label: 'Nepal', value: 'NP' },
  { label: 'Netherlands', value: 'NL' },
  { label: 'New Caledonia', value: 'NC' },
  { label: 'New Zealand', value: 'NZ' },
  { label: 'Nicaragua', value: 'NI' },
  { label: 'Niger', value: 'NE' },
  { label: 'Nigeria', value: 'NG' },
  { label: 'Niue', value: 'NU' },
  { label: 'Norfolk Island', value: 'NF' },
  { label: 'Northern Mariana Islands', value: 'MP' },
  { label: 'Norway', value: 'NO' },
  { label: 'Oman', value: 'OM' },
  { label: 'Pakistan', value: 'PK' },
  { label: 'Palau', value: 'PW' },
  { label: 'Palestine, State of', value: 'PS' },
  { label: 'Panama', value: 'PA' },
  { label: 'Papua New Guinea', value: 'PG' },
  { label: 'Paraguay', value: 'PY' },
  { label: 'Peru', value: 'PE' },
  { label: 'Philippines', value: 'PH' },
  { label: 'Pitcairn', value: 'PN' },
  { label: 'Poland', value: 'PL' },
  { label: 'Portugal', value: 'PT' },
  { label: 'Puerto Rico', value: 'PR' },
  { label: 'Qatar', value: 'QA' },
  { label: 'Réunion', value: 'RE' },
  { label: 'Romania', value: 'RO' },
  { label: 'Russian Federation', value: 'RU' },
  { label: 'Rwanda', value: 'RW' },
  { label: 'Saint Barthélemy', value: 'BL' },
  { label: 'Saint Helena, Ascension and Tristan da Cunha', value: 'SH' },
  { label: 'Saint Kitts and Nevis', value: 'KN' },
  { label: 'Saint Lucia', value: 'LC' },
  { label: 'Saint Martin (French part)', value: 'MF' },
  { label: 'Saint Pierre and Miquelon', value: 'PM' },
  { label: 'Saint Vincent and the Grenadines', value: 'VC' },
  { label: 'Samoa', value: 'WS' },
  { label: 'San Marino', value: 'SM' },
  { label: 'Sao Tome and Principe', value: 'ST' },
  { label: 'Saudi Arabia', value: 'SA' },
  { label: 'Senegal', value: 'SN' },
  { label: 'Serbia', value: 'RS' },
  { label: 'Seychelles', value: 'SC' },
  { label: 'Sierra Leone', value: 'SL' },
  { label: 'Singapore', value: 'SG' },
  { label: 'Sint Maarten (Dutch part)', value: 'SX' },
  { label: 'Slovakia', value: 'SK' },
  { label: 'Slovenia', value: 'SI' },
  { label: 'Solomon Islands', value: 'SB' },
  { label: 'Somalia', value: 'SO' },
  { label: 'South Africa', value: 'ZA' },
  { label: 'South Georgia and the South Sandwich Islands', value: 'GS' },
  { label: 'South Sudan', value: 'SS' },
  { label: 'Spain', value: 'ES' },
  { label: 'Sri Lanka', value: 'LK' },
  { label: 'Sudan', value: 'SD' },
  { label: 'Suriname', value: 'SR' },
  { label: 'Svalbard and Jan Mayen', value: 'SJ' },
  { label: 'Swaziland', value: 'SZ' },
  { label: 'Sweden', value: 'SE' },
  { label: 'Switzerland', value: 'CH' },
  { label: 'Syrian Arab Republic', value: 'SY' },
  { label: 'Taiwan', value: 'TW' },
  { label: 'Tajikistan', value: 'TJ' },
  { label: 'Tanzania, United Republic of', value: 'TZ' },
  { label: 'Thailand', value: 'TH' },
  { label: 'Timor-Leste', value: 'TL' },
  { label: 'Togo', value: 'TG' },
  { label: 'Tokelau', value: 'TK' },
  { label: 'Tonga', value: 'TO' },
  { label: 'Trinidad and Tobago', value: 'TT' },
  { label: 'Tunisia', value: 'TN' },
  { label: 'Turkey', value: 'TR' },
  { label: 'Turkmenistan', value: 'TM' },
  { label: 'Turks and Caicos Islands', value: 'TC' },
  { label: 'Tuvalu', value: 'TV' },
  { label: 'Uganda', value: 'UG' },
  { label: 'Ukraine', value: 'UA' },
  { label: 'United Arab Emirates', value: 'AE' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'United States Minor Outlying Islands', value: 'UM' },
  { label: 'Uruguay', value: 'UY' },
  { label: 'Uzbekistan', value: 'UZ' },
  { label: 'Vanuatu', value: 'VU' },
  { label: 'Venezuela, Bolivarian Republic of', value: 'VE' },
  { label: 'Viet Nam', value: 'VN' },
  { label: 'Virgin Islands, British', value: 'VG' },
  { label: 'Virgin Islands, U.S.', value: 'VI' },
  { label: 'Wallis and Futuna', value: 'WF' },
  { label: 'Western Sahara', value: 'EH' },
  { label: 'Yemen', value: 'YE' },
  { label: 'Zambia', value: 'ZM' },
  { label: 'Zimbabwe', value: 'ZW' },
];

export const GxPStartEvent = [
  {
    label: 'POP',
    value: 'signature',
  },
  {
    label: 'Scan',
    value: 'scan',
    disabled: true,
  },
  {
    label: 'Location geofence',
    value: 'location_geofence',
    disabled: true,
  },
];

export const GxPEndEvent = [
  {
    label: 'POD',
    value: 'signature',
  },
  {
    label: 'Scan',
    value: 'scan',
    disabled: true,
  },
  {
    label: 'Location geofence',
    value: 'location_geofence',
    disabled: true,
  },
];

export const GxPSettingsOffset = [
  { label: '0 min', value: '0' },
  { label: '10 min', value: '10' },
  { label: '20 min', value: '20' },
  { label: '30 min', value: '30' },
];

export const AutoReleaseOptions = [
  {
    value: 'NO_TEMPERATURE_EXCURSION',
    label: 'No temperature excursion',
  },
  {
    value: 'DATA_COMPLETE',
    label: 'Data complete',
  },
  {
    value: 'AT_LEAST_ONE_RTTM_LOGGER',
    label: 'At least one RTTM logger',
  },
  {
    value: 'SINGLE_DEVICE_TYPE',
    label: 'Single device type',
  },
  {
    value: 'CALIBRATION_VALID_AND_IN_DATE',
    label: 'Calibration on record',
  },
  {
    value: 'MKT_WITHIN_LIMIT',
    label: 'MKT within limit',
  },
];

export const GxPSortOptions = [
  { key: 'delivery_date', label: 'Delivery date' },
  {
    key: 'last_status_update',
    label: `Last ${themeT('statusUpdates', theme.themeType)?.toLowerCase()}`,
  },
  { key: 'excursions', label: 'Excursions' },
];
// COC comment types reference types
export const typeMapping: Record<number, string> = {
  1: 'ChainOfCustodyComments',
  2: 'ChainOfCustody',
  3: 'ShipmentAlert',
  4: 'ChainOfCustodyFile',
  7: 'ShipmentAlert',
  10: 'ChainOfCustody',
};
// file download types for coc files
export const previewableTypes = [
  // Documents
  'pdf',
  'txt',
  'csv',
  'json',
  'xml',
  // Images
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
];
export const shipmentSortOptions = [
  { key: 'scheduled_delivery_date', label: 'Delivery date' },
  { key: 'scheduled_pickup_date', label: 'Pickup date' },
  { key: 'created_at', label: 'Created date' },
  {
    key: 'last_status_update',
    label: 'Last updated',
  },
  {
    key: 'courier_name',
    label: `${themeT('transportProvider', theme.themeType)}`,
  },
  { key: 'team', label: 'Primary team' },
  { key: 'alert_severity', label: 'Alert severity' },
  { key: 'shipment_status', label: 'Shipment status' },
  { key: 'alert_count', label: '# Alerts' },
  { key: 'alert_age', label: 'Alert age' },
];

export const currencyOptions = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
];

export const dateFormatOptions = [
  {
    value: 'MM/DD/YYYY',
    label: 'MM/DD/YYYY - 07/27/2026',
  },
  {
    value: 'DD/MM/YYYY',
    label: 'DD/MM/YYYY - 27/07/2026',
  },
  {
    value: 'YYYY/MM/DD',
    label: 'YYYY/MM/DD - 2026/07/27',
  },
];

// Help center "Still stuck?" rows -- fixed actions, not served by the backend.
export const stillStuckData: StillStuckItem[] = [
  {
    id: 'tour',
    title: 'Watch the 5 min tour',
    description: 'A short walkthrough of the platform',
  },
  {
    id: 'assistant',
    title: 'Ask the help assistant',
    description: 'Answers from the help articles, with links',
  },
  {
    id: 'chat',
    title: 'Chat with support',
    description: 'Talk to the Zoomlogi team',
  },
  {
    id: 'email',
    title: 'Email support',
    description: 'support@zoomlogi.com',
    copy_value: 'support@zoomlogi.com',
  },
];

// Help assistant empty-state suggestions (PRD: how-to questions only, never data questions).
export const helpAssistantSuggestions: string[] = [
  'How do I set up a geofence?',
  'Why is my SMS notification not arriving?',
  'What does Use AI do on an alert?',
  'How do I pin a filter as a tab?',
];
